import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { MdLocationOn, MdOutlinePeople, MdWorkOutline, MdCalendarToday } from "react-icons/md";
import { ArrowLeft, Share2, UserPlus } from "lucide-react";
import Header from "../../Header";
import LeftNavBar from "../../LeftNavBar";
import PageHeader from "../../PageHeader";
import { PageHeaderName } from "../../../StyledCss/styledComponents";
import { PrimaryButton, SecondaryButton } from "../../../StyledCss/styledbtn";
import ReferCandidateModal from "../ReferCandidateModal";
import ShareJobModal from "../ShareJobModal";
import { getHiringJobById } from "../../../api/services/hroneApi";
import { normalizeJob } from "../../../util/hiringUtils";
import { usePermissions } from "../../../redux/slices/permissionsSlice";
import { FEATURE_CODES } from "../../../util/featureCodes";
import "../JobDetailsPage/index.css";
import "./index.css";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const formatPostedDate = (dateStr) => {
  if (!dateStr) return null;
  const [day, month, year] = dateStr.split("-");
  return `${parseInt(day, 10)} ${MONTHS[parseInt(month, 10) - 1]} ${year}`;
};

// Same rules as JobDetailsPage add/refer gating (closed / future post date).
const isFutureDate = (dateStr) => {
  if (!dateStr) return false;
  const [day, month, year] = dateStr.split("-").map((n) => parseInt(n, 10));
  const postDate = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return postDate > today;
};

const getReferBlockReason = (job) => {
  if (!job) return "Job not found";
  if (job.status === "CLOSED") return "Cannot add or refer candidates to a closed job";
  if (job.status === "DRAFT") return "Cannot add or refer candidates to a draft job";
  if (isFutureDate(job.publishedDate)) return "Cannot add or refer candidates before the job post date";
  return null;
};

const EmployeeJobDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { hasPermission } = usePermissions();
  const canCreateCandidate = hasPermission(FEATURE_CODES.HIRING_CANDIDATE_CREATE);

  const [job, setJob] = useState(null);
  const [jobLoading, setJobLoading] = useState(true);
  const [jobNotFound, setJobNotFound] = useState(false);
  const [isReferOpen, setIsReferOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);

  const orgDetails = JSON.parse(localStorage.getItem("org"));
  const primaryColor = orgDetails?.primaryColor || "#e86020";

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setJobLoading(true);
      try {
        const res = await getHiringJobById(id);
        const data = await res.json();
        if (!cancelled) {
          if (data.status) {
            setJob(normalizeJob(data.data));
          } else {
            setJobNotFound(true);
          }
        }
      } catch {
        if (!cancelled) setJobNotFound(true);
      } finally {
        if (!cancelled) setJobLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id]);

  const handleBack = () => navigate("/hiring/openings");

  if (jobLoading) {
    return (
      <>
        <Header />
        <div className="d-flex" style={{ "--hiring-primary": primaryColor }}>
          <LeftNavBar />
          <div className="page-content-wrapper d-flex justify-content-center align-items-center">
            <p style={{ color: "#9e9e9e", fontSize: "15px" }}>Loading job details…</p>
          </div>
        </div>
      </>
    );
  }

  if (!job || jobNotFound) {
    return (
      <>
        <Header />
        <div className="d-flex" style={{ "--hiring-primary": primaryColor }}>
          <LeftNavBar />
          <div className="page-content-wrapper d-flex justify-content-center align-items-center">
            <div style={{ textAlign: "center", color: "#646569" }}>
              <p style={{ fontSize: "18px", fontWeight: 500 }}>Job not found</p>
              <button className="job-details-back-btn" style={{ margin: "10px auto" }} onClick={handleBack}>
                <ArrowLeft size={16} /> Back to Job Openings
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="d-flex" style={{ "--hiring-primary": primaryColor }}>
        <LeftNavBar />
        <div className="page-content-wrapper">
          <PageHeader>
            <PageHeaderName>
              <ArrowLeft size={20} onClick={handleBack} style={{ cursor: "pointer", marginRight: "8px" }} />
              Job Openings
            </PageHeaderName>
          </PageHeader>

          <div className="job-details-summary-card job-details-summary-header">
            <div className="job-details-title-row">
              <div>
                <p className="job-details-title">{job.title}</p>
                <p className="job-details-dept">{job.designation}</p>
              </div>
              <span className="job-type-badge">{job.employmentType}</span>
            </div>

            <div className="job-details-float-actions">
              {canCreateCandidate && !getReferBlockReason(job) && (
                <PrimaryButton
                  type="button"
                  width="auto"
                  onClick={() => {
                    const blockReason = getReferBlockReason(job);
                    if (blockReason) {
                      toast.error(blockReason);
                      return;
                    }
                    setIsReferOpen(true);
                  }}
                >
                  <UserPlus size={16} style={{ marginRight: "6px", verticalAlign: "-3px" }} />
                  Refer a Candidate
                </PrimaryButton>
              )}
              {job.status !== "CLOSED" && (
                <SecondaryButton type="button" width="auto" onClick={() => setIsShareOpen(true)}>
                  <Share2 size={16} style={{ marginRight: "6px", verticalAlign: "-3px" }} />
                  Share
                </SecondaryButton>
              )}
            </div>

            <div className="job-details-meta-row">
              <span className="job-details-meta-pill">
                <MdLocationOn className="job-details-meta-icon" />{job.location}
              </span>
              <span className="job-details-meta-pill">
                <MdOutlinePeople className="job-details-meta-icon" />
                {job.vacancies} {job.vacancies === 1 ? "Vacancy" : "Vacancies"}
              </span>
              <span className="job-details-meta-pill">
                <MdWorkOutline className="job-details-meta-icon" />{job.experience}
              </span>
            </div>

            {job.publishedDate && (
              <div className="job-details-posted-date">
                <MdCalendarToday className="job-details-meta-icon" />
                Posted on {formatPostedDate(job.publishedDate)}
              </div>
            )}
          </div>

          <div className="job-details-content-box">
            <div className="job-details-overview">
              <div className="job-details-overview-section">
                <p className="job-details-overview-section-title">Job Description</p>
                <p className="job-details-description">{job.description}</p>
              </div>
              <div className="job-details-overview-section">
                <p className="job-details-overview-section-title">Required Skills</p>
                <div className="job-details-skills-list">
                  {job.skills.map((skill) => (
                    <span
                      key={skill}
                      className="job-details-skill-tag"
                      style={{
                        backgroundColor: primaryColor + "0d",
                        borderColor: primaryColor + "40",
                        color: primaryColor,
                      }}
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
              <div className="job-details-overview-section">
                <p className="job-details-overview-section-title">Role Details</p>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {[
                    { label: "Designation", value: job.designation },
                    { label: "Location", value: job.location },
                    { label: "Employment Type", value: job.employmentType },
                    { label: "Vacancies", value: job.vacancies },
                    { label: "Experience", value: job.experience },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ display: "flex", gap: "12px" }}>
                      <span style={{ width: "150px", fontSize: "13px", color: "#646569", fontWeight: 500 }}>{label}</span>
                      <span style={{ fontSize: "14px", color: "#212121" }}>{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ReferCandidateModal
        jobId={id}
        isOpen={isReferOpen}
        onClose={() => setIsReferOpen(false)}
        onSuccess={() => setIsReferOpen(false)}
      />

      <ShareJobModal
        job={job}
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
      />
    </>
  );
};

export default EmployeeJobDetailsPage;
